import React, { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import {
  Link2, Loader2, ExternalLink, ChevronDown, RefreshCw, Copy, LifeBuoy,
  MessageCircle, Zap, Send, Bot, Clock,
} from "lucide-react";
import {
  AppButton, AppCard, AppCardHeader, AppCardTitle, AppCardDescription, AppCardContent,
  Callout, Checkbox, ProgressBar, WhatsAppIcon, cn,
} from "../ui";

/**
 * WhatsAppConnectGuide — what an institute sees on the WhatsApp screen until a
 * number is connected: what they need, what the Facebook window will ask, and
 * what to do after.
 *
 * Almost every failed connect is a missing prerequisite discovered halfway
 * through Meta's dialog — a number still registered on the WhatsApp app, no
 * admin role on the business portfolio, a blocked pop-up — and Meta's own error
 * for each is a dead end. Listing them before the button is the cheapest fix.
 *
 * The prerequisites depend on the connect flow. Embedded Signup creates the
 * WhatsApp Business Account inside the Facebook window; the redirect dialog can
 * only share an account that already exists, so it adds one requirement and the
 * steps differ.
 *
 * The readiness ticks are a per-viewer self-check, kept in this browser only.
 * They never gate the button — the owner may know better than the list.
 */

/* The same console pages vc_be/services/whatsappHealthService.js links to. */
const META_LINKS = {
  billing: "https://business.facebook.com/billing_hub/payment_settings",
  verification: "https://business.facebook.com/settings/security",
  whatsappManager: "https://business.facebook.com/wa/manage/home/",
};

const CAPABILITIES = [
  { Icon: MessageCircle, label: "One shared inbox for every reply" },
  { Icon: Zap, label: "Automatic class and fee reminders" },
  { Icon: Send, label: "Broadcasts to batches and groups" },
  { Icon: Bot, label: "AI replies to common questions" },
];

const prerequisitesFor = (flow) => [
  {
    id: "facebook",
    title: "A Facebook account that manages your business",
    detail: flow === "oauth_redirect"
      ? "Sign in with an account that is an admin of the Meta business portfolio (Business Manager) that owns your WhatsApp Business Account."
      : "Sign in with an account that is an admin of your institute's Meta business portfolio (Business Manager). No portfolio yet? You can create one during setup.",
  },
  ...(flow === "oauth_redirect"
    ? [{
        id: "waba",
        title: "A WhatsApp Business Account with your number on it",
        detail: "Create it in WhatsApp Manager and verify your number there first — this connection shares an existing account. If it holds several numbers, the first one is connected.",
        link: { href: META_LINKS.whatsappManager, label: "Open WhatsApp Manager" },
      }]
    : []),
  {
    id: "number",
    title: "A phone number for WhatsApp",
    detail: "One you own that can receive an SMS or a voice call for a verification code. Mobile or landline both work. A number can be connected to one institute only.",
  },
  {
    id: "not-on-app",
    title: "That number is not on the WhatsApp app",
    detail: "If it is registered on WhatsApp or WhatsApp Business on a phone, delete that account first (Settings → Account → Delete my account) and wait a few minutes. Chats on that phone are not carried over.",
  },
  {
    id: "details",
    title: "Your institute's name, website and address",
    detail: "Meta uses them for your business profile.",
  },
  {
    id: "display-name",
    title: "A display name",
    detail: "The name students see above your messages. It should match your institute's name, for example “Sunrise Academy”. Meta reviews it before it is shown.",
  },
  {
    id: "popups",
    title: "Pop-ups allowed in this browser",
    detail: "Setup continues in a Facebook window. Allow pop-ups for this site and pause any ad blocker while you connect.",
  },
];

const stepsFor = (flow) => (flow === "oauth_redirect"
  ? [
      { title: "Open the Facebook window", detail: "Click Connect WhatsApp. Setup continues in a secure Facebook window." },
      { title: "Sign in", detail: "Use the Facebook account that manages your WhatsApp Business Account." },
      { title: "Choose what to share", detail: "Select your business and the WhatsApp Business Account that holds your number." },
      { title: "Allow access", detail: "Confirm the permissions so messages can be sent and received on your behalf." },
      { title: "We finish the setup", detail: "We register your number, start receiving replies and sync your templates. Your inbox opens right after." },
    ]
  : [
      { title: "Open the Facebook window", detail: "Click Connect WhatsApp. Setup continues in a secure Facebook window." },
      { title: "Sign in and choose your business", detail: "Pick your Meta business portfolio, or create one with your institute's details." },
      { title: "Set up your WhatsApp Business profile", detail: "Create or select a WhatsApp Business Account and enter your display name." },
      { title: "Add and verify your number", detail: "Enter the number, then the code Meta sends by SMS or voice call." },
      { title: "Allow access", detail: "Confirm the permissions so messages can be sent and received on your behalf." },
      { title: "We finish the setup", detail: "We register your number, start receiving replies and sync your templates. Your inbox opens right after." },
    ]);

const AFTER_CONNECT = [
  {
    title: "Add a payment method",
    detail: "Meta bills template messages — reminders and broadcasts — to a card on your WhatsApp Business Account, and does not deliver them without one. Replies within 24 hours of a student's message are free.",
    link: { href: META_LINKS.billing, label: "Payment settings" },
  },
  {
    title: "Verify your business (recommended)",
    detail: "Until it is verified, Meta limits how many people you can start a conversation with each day — usually 250.",
    link: { href: META_LINKS.verification, label: "Security Center" },
  },
  {
    title: "Get your templates approved",
    detail: "A message that starts a conversation has to use a template Meta has approved. Create them in the Templates tab; review usually takes minutes, sometimes a day.",
  },
  {
    title: "Turn on automations",
    detail: "Send class-started and fee reminders automatically from the Automation tab.",
  },
];

const TROUBLESHOOTING = [
  {
    q: "Nothing happened when I clicked Connect",
    a: "The browser most likely blocked the Facebook window. Allow pop-ups for this site — look for the blocked-window icon at the end of the address bar — pause any ad blocker, and try again.",
  },
  {
    q: "Meta says my number is already registered",
    a: "The number is still on the WhatsApp or WhatsApp Business app. Delete that account in the app, wait a few minutes, then start again.",
  },
  {
    q: "The verification code did not arrive",
    a: "Check the country code, then choose Voice call instead of SMS. Meta limits how often a code can be requested, so after several attempts wait a while before trying again.",
  },
  {
    q: "“This WhatsApp number is already connected to another institute”",
    a: "A number can belong to one institute only. Disconnect it there first, or connect a different number.",
  },
  {
    q: "I closed the Facebook window partway through",
    a: "Click Connect WhatsApp again. Anything already created — your business portfolio or WhatsApp Business Account — is kept, so you can select it instead of creating it again.",
  },
  {
    q: "Connected, but replies are not showing up",
    a: "Open this Config tab and use Repair Webhook. It re-subscribes this dashboard to your number's incoming messages.",
  },
];

const READY_STORAGE_KEY = "vc-wa-connect-ready";

const readReady = () => {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(READY_STORAGE_KEY) || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeReady = (ids) => {
  try {
    window.localStorage.setItem(READY_STORAGE_KEY, JSON.stringify(ids));
  } catch { /* a blocked store only costs the ticks on reload */ }
};

const ExternalAnchor = ({ href, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noreferrer noopener"
    className="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-font-medium tw-text-primary tw-no-underline hover:tw-underline"
  >
    {children} <ExternalLink size={12} aria-hidden="true" />
  </a>
);

/**
 * Props:
 *   flow                 "embedded_signup" | "oauth_redirect"
 *   canManage            false → no Connect button; names who can
 *   connectAvailable     false → the platform has no Meta app configured
 *   previousPhoneNumber  set when this institute was connected before
 *   busy                 the code exchange is running
 *   awaitingReturn       a Facebook window is open and we are waiting on it
 *   checking             a manual re-check is running
 *   onConnect, onCheckAgain, onCopyLink
 */
const WhatsAppConnectGuide = ({
  flow = "embedded_signup",
  canManage = true,
  connectAvailable = true,
  previousPhoneNumber = "",
  busy = false,
  awaitingReturn = false,
  checking = false,
  onConnect,
  onCheckAgain,
  onCopyLink,
}) => {
  const prerequisites = prerequisitesFor(flow);
  const steps = stepsFor(flow);
  const [ready, setReady] = useState(readReady);

  const toggleReady = useCallback((id) => {
    setReady((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      writeReady(next);
      return next;
    });
  }, []);

  const readyCount = prerequisites.filter((p) => ready.includes(p.id)).length;
  const allReady = readyCount === prerequisites.length;

  const notice = (() => {
    if (!canManage) {
      return (
        <Callout tone="info">
          Only your institute's Super Admin can connect WhatsApp, because it links your
          institute's Facebook business account. Share this page with them — everything
          they need is below.
        </Callout>
      );
    }
    if (!connectAvailable) {
      return (
        <Callout tone="warning">
          WhatsApp connect is not switched on for this platform yet.{" "}
          <Link to="/support/tickets">Raise a support ticket</Link> and we will enable it.
        </Callout>
      );
    }
    if (awaitingReturn) {
      return (
        <Callout tone="info" role="status">
          Finish setup in the Facebook window. This page updates when you come back to it.
        </Callout>
      );
    }
    if (previousPhoneNumber) {
      return (
        <Callout tone="info">
          WhatsApp was disconnected. Your conversations, templates and settings are still
          here — reconnect <strong>{previousPhoneNumber}</strong> to pick up where you left off.
        </Callout>
      );
    }
    return null;
  })();

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <AppCard>
        <AppCardContent>
          <div className="tw-flex tw-flex-col tw-gap-5 md:tw-flex-row md:tw-items-start">
            <span
              className="tw-flex tw-h-14 tw-w-14 tw-shrink-0 tw-items-center tw-justify-center tw-rounded tw-bg-surface-alt"
              aria-hidden="true"
            >
              <WhatsAppIcon size={32} />
            </span>

            <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-4">
              <div>
                <h2 className="tw-mb-1 tw-text-lg tw-font-semibold tw-leading-snug tw-text-content">
                  {previousPhoneNumber ? "Reconnect WhatsApp Business" : "Connect WhatsApp Business"}
                </h2>
                <p className="tw-m-0 tw-text-sm tw-leading-relaxed tw-text-content-muted">
                  Message students and parents from your institute's own WhatsApp number.
                </p>
              </div>

              <ul className="tw-m-0 tw-grid tw-list-none tw-grid-cols-1 tw-gap-2 tw-p-0 sm:tw-grid-cols-2">
                {CAPABILITIES.map(({ Icon, label }) => (
                  <li key={label} className="tw-flex tw-items-center tw-gap-2 tw-text-[0.8125rem] tw-text-content">
                    <Icon size={15} className="tw-shrink-0 tw-text-primary" aria-hidden="true" />
                    {label}
                  </li>
                ))}
              </ul>

              {notice}

              {canManage && connectAvailable && (
                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-2">
                  <AppButton onClick={onConnect} disabled={busy}>
                    {busy
                      ? <><Loader2 size={16} className="tw-animate-spin" aria-hidden="true" /> Finishing setup…</>
                      : <><Link2 size={16} aria-hidden="true" /> {previousPhoneNumber ? "Reconnect WhatsApp" : "Connect WhatsApp"}</>}
                  </AppButton>
                  {awaitingReturn ? (
                    <AppButton variant="outline" onClick={onCheckAgain} disabled={checking}>
                      <RefreshCw size={16} className={cn(checking && "tw-animate-spin")} aria-hidden="true" />
                      {checking ? "Checking…" : "I've finished — check again"}
                    </AppButton>
                  ) : (
                    <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-content-subtle">
                      <Clock size={13} aria-hidden="true" /> About 10 minutes · you'll sign in with Facebook
                    </span>
                  )}
                </div>
              )}

              {!canManage && onCopyLink && (
                <div>
                  <AppButton variant="outline" size="sm" onClick={onCopyLink}>
                    <Copy size={16} aria-hidden="true" /> Copy link to this page
                  </AppButton>
                </div>
              )}
            </div>
          </div>
        </AppCardContent>
      </AppCard>

      <div className="tw-grid tw-grid-cols-1 tw-items-start tw-gap-4 lg:tw-grid-cols-2">
        <AppCard>
          <AppCardHeader>
            <AppCardTitle>Before you start</AppCardTitle>
            <AppCardDescription>
              Have these ready and the Facebook window takes a few minutes.
            </AppCardDescription>
          </AppCardHeader>
          <AppCardContent>
            <div className="tw-mb-4 tw-flex tw-items-center tw-gap-3">
              <ProgressBar
                value={(readyCount / prerequisites.length) * 100}
                color="emerald"
                size="sm"
                className="tw-flex-1"
              />
              <span className="tw-shrink-0 tw-text-xs tw-font-medium tw-text-content-muted" aria-live="polite">
                {allReady ? "All set" : `${readyCount} of ${prerequisites.length} ready`}
              </span>
            </div>
            <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-2 tw-p-0">
              {prerequisites.map((item) => {
                const ticked = ready.includes(item.id);
                return (
                  <li
                    key={item.id}
                    className={cn(
                      "tw-rounded-sm tw-border tw-border-solid tw-p-3 tw-transition-colors",
                      ticked ? "tw-border-success/40 tw-bg-success/5" : "tw-border-line"
                    )}
                  >
                    <Checkbox
                      checked={ticked}
                      onChange={() => toggleReady(item.id)}
                      label={item.title}
                      labelClassName="tw-font-medium"
                      description={item.detail}
                    />
                    {item.link && (
                      <div className="tw-mt-1.5 tw-pl-6">
                        <ExternalAnchor href={item.link.href}>{item.link.label}</ExternalAnchor>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </AppCardContent>
        </AppCard>

        <div className="tw-flex tw-flex-col tw-gap-4">
          <AppCard>
            <AppCardHeader>
              <AppCardTitle>How connecting works</AppCardTitle>
              <AppCardDescription>What the Facebook window will ask, in order.</AppCardDescription>
            </AppCardHeader>
            <AppCardContent>
              <ol className="tw-m-0 tw-list-none tw-p-0">
                {steps.map((step, i) => (
                  <li key={step.title} className="tw-relative tw-flex tw-gap-3 tw-pb-4 last:tw-pb-0">
                    {i < steps.length - 1 && (
                      <span aria-hidden="true" className="tw-absolute tw-bottom-0 tw-left-3.5 tw-top-8 tw-w-px tw-bg-line" />
                    )}
                    <span
                      aria-hidden="true"
                      className="tw-flex tw-h-7 tw-w-7 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-primary-soft tw-text-xs tw-font-semibold tw-text-primary-soft-fg"
                    >
                      {i + 1}
                    </span>
                    <div className="tw-min-w-0 tw-pt-0.5">
                      <p className="tw-mb-0.5 tw-text-sm tw-font-medium tw-text-content">{step.title}</p>
                      <p className="tw-m-0 tw-text-[0.8125rem] tw-leading-normal tw-text-content-muted">{step.detail}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </AppCardContent>
          </AppCard>

          <AppCard>
            <AppCardHeader>
              <AppCardTitle>After you connect</AppCardTitle>
              <AppCardDescription>What to finish so your messages reach students.</AppCardDescription>
            </AppCardHeader>
            <AppCardContent>
              <ol className="tw-m-0 tw-flex tw-flex-col tw-gap-3 tw-pl-5">
                {AFTER_CONNECT.map((item) => (
                  <li key={item.title} className="tw-text-sm tw-text-content">
                    <p className="tw-mb-0.5 tw-text-sm tw-font-medium tw-text-content">{item.title}</p>
                    <p className="tw-m-0 tw-text-[0.8125rem] tw-leading-normal tw-text-content-muted">{item.detail}</p>
                    {item.link && (
                      <div className="tw-mt-1">
                        <ExternalAnchor href={item.link.href}>{item.link.label}</ExternalAnchor>
                      </div>
                    )}
                  </li>
                ))}
              </ol>
            </AppCardContent>
          </AppCard>
        </div>
      </div>

      <AppCard>
        <AppCardHeader>
          <AppCardTitle>If something goes wrong</AppCardTitle>
        </AppCardHeader>
        <AppCardContent>
          {TROUBLESHOOTING.map(({ q, a }, i) => (
            <details
              key={q}
              className={cn("tw-group", i > 0 && "tw-border-0 tw-border-t tw-border-solid tw-border-line")}
            >
              <summary className="tw-flex tw-cursor-pointer tw-list-none tw-items-center tw-justify-between tw-gap-3 tw-rounded-sm tw-py-3 tw-text-sm tw-font-medium tw-text-content tw-focus-ring [&::-webkit-details-marker]:tw-hidden">
                {q}
                <ChevronDown
                  size={16}
                  aria-hidden="true"
                  className="tw-shrink-0 tw-text-content-subtle tw-transition-transform group-open:tw-rotate-180"
                />
              </summary>
              <p className="tw-mb-3 tw-mt-0 tw-text-[0.8125rem] tw-leading-relaxed tw-text-content-muted">{a}</p>
            </details>
          ))}
          <p className="tw-mb-0 tw-mt-3 tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-content-muted">
            <LifeBuoy size={14} aria-hidden="true" />
            Still stuck? <Link to="/support/tickets">Raise a support ticket</Link> with the message Facebook showed you.
          </p>
        </AppCardContent>
      </AppCard>
    </div>
  );
};

export default WhatsAppConnectGuide;
